import { createContext, useContext, ReactNode } from 'react';
import { useTeams } from '@/hooks/useTeams';
import { Team, ModuleId } from '@/types/team';

interface TeamContextType {
  teams: Team[];
  teamMemberships: { teamId: string; role: 'admin' | 'member' }[];
  currentTeam: Team | null;
  currentTeamId: string | null;
  setCurrentTeamId: (id: string | null) => void;
  addTeam: (name: string) => Promise<string | null>;
  updateTeamSlogan: (teamId: string, slogan: string) => Promise<void>;
  updateTeamName: (teamId: string, name: string) => Promise<void>;
  updateTeamImage: (teamId: string, imageUrl: string | null) => Promise<void>;
  updateTeamSlug: (teamId: string, slug: string) => Promise<boolean>;
  updateWeekendWindow: (teamId: string, settings: {
    weekendStartDay?: number;
    weekendStartHour?: number;
    weekendEndDay?: number;
    weekendEndHour?: number;
  }) => Promise<void>;
  updateDayPeriodHours: (teamId: string, settings: {
    morningHour?: number;
    eveningHour?: number;
  }) => Promise<void>;
  updateModuleVisibility: (teamId: string, settings: {
    showFypModule?: boolean;
    showTasksModule?: boolean;
    showCompletedModule?: boolean;
    showGroceryModule?: boolean;
    showStockModule?: boolean;
    showGoalsModule?: boolean;
  }) => Promise<void>;
  updateModuleOrder: (teamId: string, moduleOrder: ModuleId[]) => Promise<void>;
  updateTeamModuleLabels: (teamId: string, moduleLabels: Team['moduleLabels']) => Promise<void>;
  updateTeamTagIcons: (teamId: string, tagIcons: Team['tagIcons']) => Promise<void>;
  updateTeamLanguage: (teamId: string, language: 'nl' | 'en') => Promise<void>;
  updateTeamVisibility: (teamId: string, isPublic: boolean) => Promise<void>;
  searchPublicTeams: (query: string) => Promise<Team[]>;
  requestTeamAccess: (teamId: string) => Promise<boolean>;
  updateTeamTheme: (teamId: string, settings: {
    themePrimaryColor?: string;
    actionButtonBgColor?: string;
    actionButtonIconColor?: string;
  }) => Promise<void>;
  updateInviteCode: (teamId: string, code: string) => Promise<boolean>;
  updateInviteCodeEnabled: (teamId: string, enabled: boolean) => Promise<void>;
  joinTeamByCode: (code: string) => Promise<{ success: boolean; teamId?: string; teamName?: string; alreadyMember?: boolean }>;
  isTeamAdmin: (teamId: string) => boolean;
  isTeamMember: (teamId: string) => boolean;
  loading: boolean;
  userId: string | null;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export function TeamProvider({ children }: { children: ReactNode }) {
  const teamData = useTeams();

  return (
    <TeamContext.Provider value={teamData}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}
